import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import { Edit20Px, useToast } from "@qubit/autoparts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import type { WorkstationSettingsFormValues } from "~/features/preview/workstation/hooks/useWorkstationConfigForm";
import {
  getWorkstationAssignmentState,
  sortWorkstationsByGrid
} from "~/features/preview/workstation/settingsWorkstation.logic";
import {
  settingsInputMinHeightPx,
  settingsSelectSx,
  settingsSiteSelectMaxWidthPx
} from "~/features/settings/settings.styles";
import {
  selectSelectedGridId,
  setDropdownWorkstationId
} from "~/features/workstationConfig/workstationConfig.slice";
import { useSetThisWorkstation } from "~/hooks/useSetThisWorkstation";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { selectDropdownWorkstation } from "~/redux/selectors/workstationConfigSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import { EditWorkstationName } from "./EditWorkstationName";
import { WorkstationOverviewHelperText } from "./WorkstationOverviewHelperText";

type Props = {
  isEditingName: boolean;
  setIsEditingName: (isEditingName: boolean) => void;
};

export function SiteWorkstationAssignment({
  isEditingName,
  setIsEditingName
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { successToast, errorToast } = useToast();
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const userFc = useAppSelector(selectUsersFulfillmentCenter);
  const selectedGridId = useAppSelector(selectSelectedGridId);
  const { setThisWorkstation, isLoading: isSettingWorkstation } =
    useSetThisWorkstation();
  const [isUnassigning, setIsUnassigning] = useState(false);
  const dropdownWorkstation = useAppSelector(selectDropdownWorkstation);
  // Prefer form deviceId so Select shows the saved name before list cache refetches
  const [formWorkstationId, formDeviceId] = useWatch<
    WorkstationSettingsFormValues,
    ["workstationId", "deviceId"]
  >({
    name: ["workstationId", "deviceId"]
  });

  useEffect(() => {
    setIsEditingName(false);
  }, [dropdownWorkstation?.id, setIsEditingName]);

  const { data: workstations, isLoading } = useGetWorkstationsQuery(undefined, {
    skip: !userFc
  });
  const sortedWorkstations = useMemo(
    () =>
      workstations === undefined
        ? undefined
        : sortWorkstationsByGrid(workstations, selectedGridId),
    [workstations, selectedGridId]
  );

  const handleWorkstationChanged = useCallback(
    (e: SelectChangeEvent) => {
      dispatch(setDropdownWorkstationId(e.target.value));
    },
    [dispatch]
  );

  const {
    selectedId: dropdownWorkstationId,
    isDirty: isWorkstationDirty,
    isAssigned: isCurrentWsAssigned
  } = getWorkstationAssignmentState(
    sortedWorkstations,
    siteWorkstation?.id,
    dropdownWorkstation?.id
  );

  const handleSetThisWorkstationClicked = useCallback(async () => {
    if (isCurrentWsAssigned) {
      setIsUnassigning(true);
      try {
        await setThisWorkstation(null, true);
        dispatch(setDropdownWorkstationId(""));
        successToast(t("workstation unset"), {
          description: t("workstation unset description")
        });
      } catch (error) {
        errorToast(getMessageFromRtkError(error));
      } finally {
        setIsUnassigning(false);
      }
      return;
    }

    if (!isWorkstationDirty) {
      return;
    }
    const workstationToAssign = dropdownWorkstationId
      ? dropdownWorkstation
      : null;
    try {
      await setThisWorkstation(workstationToAssign ?? null, true);
      if (workstationToAssign) {
        successToast(t("workstation set"), {
          description: t("workstation set description", {
            workstation: workstationToAssign.deviceId
          })
        });
      } else {
        successToast(t("workstation unset"), {
          description: t("workstation unset description")
        });
      }
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  }, [
    isCurrentWsAssigned,
    isWorkstationDirty,
    setThisWorkstation,
    dropdownWorkstation,
    dropdownWorkstationId,
    dispatch,
    successToast,
    errorToast,
    t
  ]);

  const assignDisabled =
    isSettingWorkstation ||
    isUnassigning ||
    (!isWorkstationDirty && !isCurrentWsAssigned);

  const showAsUnassignButton = isCurrentWsAssigned || isUnassigning;
  const buttonLabel = showAsUnassignButton ? t("unassign") : t("assign");
  const showAssignOrUnassignButton =
    dropdownWorkstationId !== "" || isUnassigning;

  if (!sortedWorkstations && isLoading) {
    return (
      <Stack id="ws-site-assignment-loading" spacing={2}>
        <Typography variant="body2">{t("workstation")}</Typography>
      </Stack>
    );
  }

  if (!sortedWorkstations?.length && !isLoading) {
    return (
      <Stack id="ws-site-assignment-empty" spacing={2}>
        <Typography variant="body2">{t("workstation")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("no workstations found")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      role="region"
      aria-label="Site workstation assignment"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "flex-start" },
        flex: { md: "1 1 auto" },
        minWidth: 0,
        width: "100%",
        gap: 1
      }}
    >
      {isAdmin && isEditingName && dropdownWorkstation ? (
        <EditWorkstationName />
      ) : (
        <>
          <Box
            id="ws-site-assignment-select-wrap"
            sx={{
              width: "100%",
              maxWidth: {
                xs: "100%",
                md: settingsSiteSelectMaxWidthPx
              },
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              flex: { md: "0 1 auto" }
            }}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ "& > .MuiOutlinedInput-root": { marginTop: 0 } }}
            >
              <Select
                id="assign-workstation-select-preview"
                fullWidth
                variant="outlined"
                value={dropdownWorkstation?.id ?? ""}
                onChange={handleWorkstationChanged}
                displayEmpty
                inputProps={{
                  "aria-label": String(t("workstation")),
                  "aria-describedby": "ws-site-assignment-helper"
                }}
                sx={settingsSelectSx}
                renderValue={(id) => {
                  if (id === "") {
                    return (
                      <Typography
                        variant="body2"
                        component="span"
                        color="text.secondary"
                      >
                        <em>{t("no workstation selected")}</em>
                      </Typography>
                    );
                  }
                  const ws = sortedWorkstations?.find((w) => w.id === id);
                  if (!ws) {
                    return "";
                  }
                  const displayName =
                    id === formWorkstationId && formDeviceId
                      ? formDeviceId
                      : ws.deviceId;
                  return (
                    <Typography variant="body2" component="span" noWrap>
                      {displayName}
                    </Typography>
                  );
                }}
              >
                {sortedWorkstations?.map((workstation) => (
                  <MenuItem key={workstation.id} value={workstation.id}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      width="100%"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ minWidth: 0 }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0, flex: "1 1 auto" }}
                      >
                        <Typography variant="body2" noWrap>
                          {workstation.deviceId}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {workstation.autostoreGridName}
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        label={
                          workstation.status === "Active"
                            ? t("workstation status chip in use")
                            : workstation.status
                        }
                        color={
                          workstation.status === "Active"
                            ? "warning"
                            : undefined
                        }
                      />
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <WorkstationOverviewHelperText
              siteWorkstation={siteWorkstation}
              dropdownWorkstation={
                dropdownWorkstation?.id ? dropdownWorkstation : null
              }
            />
          </Box>

          {isAdmin && showAssignOrUnassignButton && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setIsEditingName(true)}
              aria-label={t("edit workstation name")}
              sx={{
                flexShrink: 0,
                minHeight: settingsInputMinHeightPx,
                minWidth: settingsInputMinHeightPx,
                boxSizing: "border-box",
                px: 1
              }}
            >
              <Edit20Px
                style={{ fill: "currentColor" }}
                width={20}
                height={20}
              />
            </Button>
          )}

          {showAssignOrUnassignButton && (
            <Box
              id="ws-site-assignment-button-wrap"
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-end", md: "flex-start" },
                alignItems: "center",
                alignSelf: { xs: "stretch", md: "flex-start" },
                flexShrink: 0,
                minWidth: 0
              }}
            >
              <Button
                variant="contained"
                color={showAsUnassignButton ? "secondary" : "primary"}
                loading={isSettingWorkstation || isUnassigning}
                disabled={assignDisabled}
                onClick={() => void handleSetThisWorkstationClicked()}
                aria-label={buttonLabel}
                sx={{
                  flexShrink: 0,
                  minHeight: settingsInputMinHeightPx,
                  boxSizing: "border-box",
                  maxWidth: "none",
                  width: "auto"
                }}
              >
                {buttonLabel}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
